import React, { useMemo, useContext } from 'react';

import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import useStyles from './styles';

import { UserContext } from '../../Contexts/UserContext';
import { ACCESS_CONTROL_CONSTANTS } from '../../core/constants';
import InfoButton from '../InfoButton';
const { SYSTEMS, UI_ACCESS, TYPES } = ACCESS_CONTROL_CONSTANTS;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250
		}
	}
};

export default function DropDownMultiple({
	keyData,
	list,
	label,
	object,
	updateObject,
	selectBlockStyles,
	disabled,
	containerStyles,
	formControlStyles = {},
	isObjectEditor = true,
	directValue,
	updateDirectValue,
	isMandatory = false,
	showInfoButton = false,
	infoMessage = '',
	infoButtonProps = {},
	system = SYSTEMS.FRAMERATE
}) {
	const { getAccessControlSet } = useContext(UserContext);
	const classes = useStyles();
	const ListData = useMemo(() => {
		if (isObjectEditor) {
			return object && object[keyData] ? object[keyData] : [];
		}
		return directValue || [];
	}, [object, keyData, directValue, isObjectEditor]);

	const displayLabel = isMandatory ? `${label} *` : label;

	const handleChange = (event) => {
		const {
			target: { value }
		} = event;
		let updatedListData = typeof value === 'string' ? value.split(',') : value;
		try {
			if (isObjectEditor) {
				if (!object) throw new Error('Object undefined!');
				if (!keyData) throw new Error('Keydata not valid');
				updateObject((obj) => {
					let deepClonedObj = structuredClone(obj);
					return { ...deepClonedObj, [keyData]: updatedListData };
				});
			} else {
				updateDirectValue(updatedListData);
			}
		} catch (err) {
			alert(err);
			console.error(err);
		}
	};

	/**
	 * This variable contains a set of strings that signifies if this input is either visible/disabled/hidden
	 * @type {Set<string>}
	 */
	const dropDownMultipleControlledAccessSet = useMemo(() => {
		return getAccessControlSet(system, TYPES.INPUT_NAMES, keyData);
	}, [getAccessControlSet, keyData, system]);

	return (
		<>
			{!dropDownMultipleControlledAccessSet.has(UI_ACCESS.HIDE) && (
				<Box className={classes.dropDownContainer} style={containerStyles || {}}>
					<FormControl
						className={classes.formControl}
						variant="outlined"
						style={formControlStyles || {}}
					>
						<Typography className={classes.heading} fontWeight={600} variant="h6">
							{displayLabel}
							{showInfoButton && label?.trim() && (
								<InfoButton message={infoMessage} {...infoButtonProps} />
							)}
						</Typography>
						<Select
							labelId="demo-multiple-chip-label"
							id="demo-multiple-chip"
							multiple
							value={ListData}
							style={selectBlockStyles || {}}
							onChange={handleChange}
							input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
							renderValue={() => (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{isObjectEditor &&
										object[keyData] &&
										object[keyData].map((value) => <Chip key={value} label={value} />)}
									{!isObjectEditor &&
										directValue &&
										Array.isArray(directValue) &&
										directValue.map((value) => <Chip key={value} label={value} />)}
								</Box>
							)}
							MenuProps={MenuProps}
							disabled={disabled || dropDownMultipleControlledAccessSet.has(UI_ACCESS.DISABLE)}
						>
							{list.map((data) => (
								<MenuItem key={data} value={data.value || data}>
									{data.label || data}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>
			)}
		</>
	);
}
